/** A link's QR code, opened by its QR button (click only — never on hover): a 160px code on
 *  screen, and **Download PNG** saves the same code at print size. */
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Popover, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { useId, useRef } from 'react';

import type { TourSelfregLink } from '@/api/tours';

import { addressTail } from './linkWords';

/** The PNG a download saves: large enough to print (the popover shows a 160 px one). */
export const QR_DOWNLOAD_PX = 1024;

export function QrPopover({
  link,
  anchorEl,
  onClose,
}: {
  link: TourSelfregLink | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const printCanvas = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const download = () => {
    const canvas = printCanvas.current?.querySelector('canvas');
    if (!canvas || !link) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `selfreg-${link.id}.png`;
    a.click();
  };
  return (
    <Popover
      open={Boolean(link && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: { p: 1.5, display: 'grid', gap: 1, justifyItems: 'center', maxWidth: 260 },
        } as object,
      }}
    >
      {link && (
        <>
          <Typography id={titleId} variant="subtitle2">
            QR code of link {addressTail(link)}
          </Typography>
          <Box data-selfreg-qr>
            <QRCodeCanvas value={link.link} size={160} marginSize={2} title={link.link} />
          </Box>
          {/* What Download PNG saves: the same code at print size, not shown. */}
          <Box ref={printCanvas} data-selfreg-qr-print aria-hidden sx={{ display: 'none' }}>
            <QRCodeCanvas value={link.link} size={QR_DOWNLOAD_PX} marginSize={4} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', textAlign: 'center' }}>
            {link.link}
          </Typography>
          <Button size="small" startIcon={<DownloadIcon />} onClick={download}>
            Download PNG ({QR_DOWNLOAD_PX} px)
          </Button>
        </>
      )}
    </Popover>
  );
}
